import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { buildClassName } from 'utils/build-class-name';
import MenuButton from 'components/header/menu-button';
import styles from './style.module.scss';
import { headerIds, testIds } from 'constants/test-constants';
import Button, { SECONDARY_THEME } from 'components/control/button';
import { useIsMobile, useIsSmallDesktop } from 'hooks/use-size-class';
import UserIcon from 'components/icon/user-icon';
import { ExpCommercialCaTenant, getTenantById } from '@tenants';
import { LOCALE_EXP_COMMERCIAL_PATHS } from 'types/locales';
import { useTranslation } from 'next-i18next';
import ExpCommercialIcon from 'components/icon/exp-commercial-icon';
import { usePathname } from 'next/navigation';
import { EXP_COMMERCIAL_ROOT_DOMAIN, getRootDomainUrl } from 'constants/domains';
import SearchIcon from '@mui/icons-material/Search';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { TranslationConfig } from '@tenants/types/tenant';
import LoadingText from 'components/loading-text';
import { useThemeContext } from 'contexts';

//#region Types
type CommercialHeaderProps = {
  hideHeader?: boolean;
};

type MenuItem = {
  id: string;
  label: TranslationConfig;
  link?: string;
  newTab?: boolean;
};

type MenuItemsProps = {
  menuItems: readonly MenuItem[];
};

const Dropdown = dynamic(() => import('components/dropdown'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const MobileCommercialSearch = dynamic(() => import('./MobileSearch'), {
  loading: () => <></>,
  ssr: false,
});

const MenuItems = ({ menuItems }: MenuItemsProps) => {
  const { t } = useTranslation();

  return (
    <>
      {menuItems.map((menuItem: MenuItem, index: number) => (
        <a key={`${menuItem.id}-${index}`} href={menuItem.link} className={styles['menu-item']} target={menuItem.newTab ? '_blank' : '_self'} rel={menuItem.newTab ? 'noreferrer' : undefined}>
          {t(menuItem.label.key, { defaultValue: menuItem.label.defaultValue })}
        </a>
      ))}
    </>
  );
};

const DropdownTranslation = ({ menuItems }: MenuItemsProps) => {
  const { t } = useTranslation();

  const items = menuItems.map((menuItem: MenuItem) => ({
    ...menuItem,
    label: t(menuItem.label.key, { defaultValue: menuItem.label.defaultValue }),    
    // we programmatically server side navigate to avoid wordpress style conflicts
    onClick: () => window.location.assign(menuItem.link),
  }));

  return <Dropdown items={items} />;
};

const CommercialHeader = ({ hideHeader }: CommercialHeaderProps) => {
  const { tenantId } = useThemeContext();
  const tenant = getTenantById(tenantId) as ExpCommercialCaTenant;
  const menuItemsConfig = tenant.config?.header?.menuItems || [];
  const [menuItems, setMenuItems] = useState(tenant.config?.header?.menuItems || []);
  const [menuDropdownIsActive, setMenuDropdownIsActive] = useState(false);
  const closeDropdownTimeoutRef = useRef<number | null>(null);
  const pathname = usePathname();
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [logoLink, setLogoLink] = useState('/');

  // TODO: remove this when we go live with expCommercial
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { hostname } = window.location;
      if (hostname === 'legacy.expcommercial.com' && menuItems.length > 0) {
        const rootDomainUrl = getRootDomainUrl(EXP_COMMERCIAL_ROOT_DOMAIN);
        setLogoLink(`${rootDomainUrl}`);

        setMenuItems(() => {
          // Remove Listings (index 1) and Find an Expert (index 2)
          let newMenuItems = [...menuItemsConfig.slice(0, 1), ...menuItemsConfig.slice(3)];
          // Insert People after Services (at index 2)
          newMenuItems = [...newMenuItems.slice(0, 2),
            {
              id: 'people',
              label: {
                key: LOCALE_EXP_COMMERCIAL_PATHS.HEADER_MENULINKS_PEOPLE,
                defaultValue: 'People',
              },
              link: `${rootDomainUrl}/`,
            },
            ...newMenuItems.slice(2)];
          // Update links to point to WordPress
          Object.assign(newMenuItems[0], { link: `${rootDomainUrl}/become-an-advisor` });
          Object.assign(newMenuItems[1], { link: `${rootDomainUrl}/services` });
          // Object.assign(newMenuItems[2], { link: `${rootDomainUrl}/experts` });
          Object.assign(newMenuItems[4], { link: `${rootDomainUrl}/about` });
          return newMenuItems;
        });
      } else {
        setMenuItems(menuItemsConfig);
      }
    }
  }, [menuItemsConfig]);

  useEffect(() => {
    const timeoutRef = closeDropdownTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  const toggleMenuDropdown = () => {
    setMenuDropdownIsActive(prev => !prev);
  };

  const toggleSearch = () => {
    setIsSearchPanelOpen(prev => !prev);
  };

  const isMobile = useIsMobile();
  const isSmallDesktop = useIsSmallDesktop();

  // Set up outside click handler for search panel
  const searchPanelRef = useOutsideClickHandler(() => setIsSearchPanelOpen(false));

  const isBecomeAnAdvisorPage = pathname === '/become-an-advisor';
  const showSearchButton = pathname === '/listings' && isMobile;

  return (
    <div className={buildClassName(styles.component, hideHeader && styles['hide-header'])} data-testid={headerIds.header}>
      <div className={buildClassName(styles['layout-container'])}>
        <a href={logoLink} rel="noreferrer" aria-label="eXp Commercial Home"><ExpCommercialIcon /></a>
        <div className={styles.navigation}>
          <MenuItems menuItems={menuItems} />
          {isBecomeAnAdvisorPage &&
            <Button
              label={'I\'m Ready'}
              theme={'primary'}
              className={styles['join-us-ready-button']}
              onClick={() => window.open('https://joinapp.expcommercial.com/', '_blank')}
            />
          }
          {showSearchButton && (
            <SearchIcon 
              className={styles['search-icon']} 
              onClick={toggleSearch}
              aria-label="Open search"
            />
          )}
          {tenant.config?.header?.hasAdvisorSignIn &&
            <Button
              label={isSmallDesktop ? 'Advisor Sign In' : ''}
              Icon={!isSmallDesktop && UserIcon}
              theme={SECONDARY_THEME}
              className={styles['advisory-button']}
              onClick={() => window.open('https://advisorcenter.expcommercial.com', '_blank')}
              aria-label={!isSmallDesktop ? 'Advisor Sign In' : undefined}
            />
          }
          <MenuButton 
            className={styles.menu} 
            testId={testIds.menuButton} 
            onClick={toggleMenuDropdown} 
            closeMenu={() => setMenuDropdownIsActive(false)}
            aria-label={menuDropdownIsActive ? 'Close menu' : 'Open menu'}
          >
            <>
              <div className={styles['bar-container']} id="menu">
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
                <span className={buildClassName(styles.bar, menuDropdownIsActive && styles.x)} />
              </div>
              {menuDropdownIsActive && <DropdownTranslation menuItems={menuItems} />}
            </>
          </MenuButton>
        </div>
      </div>

      {/* Search Panel */}
      {isSearchPanelOpen && (
        <div className={styles.panel} ref={searchPanelRef as React.RefObject<HTMLDivElement>}>
          <MobileCommercialSearch onClose={() => setIsSearchPanelOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default CommercialHeader;
